import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import SEO from '../../common/SEO';
import GoogleMaps from '../../components/GoogleMaps';
import ServicesNavigationFooter from '../../components/ServicesNavigationFooter';
import RelatedTopics from '../../components/RelatedTopics';
import * as FiIcons from 'react-icons/fi';

const { 
  FiArrowLeft, FiCheckCircle, FiShield, FiTrendingUp, 
  FiHome, FiDollarSign, FiAlertTriangle, FiCheck, 
  FiInfo, FiThermometer, FiLayers, FiTruck, FiCloud,
  FiHelpCircle, FiList
} = FiIcons;

function StrawInsulation() {
  const articleSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": "Gebäude mit eingeblasenem Stroh: Vom Feld zur Fertigung (NRW)",
        "alternativeHeadline": "Warum diese natürliche Dämmung für den Mainstream bereit ist",
        "image": "https://mx.ausbau.nrw/img/Daemmtechnik_xl.jpg",
        "author": {
          "@type": "Person",
          "name": "Maxim Kelm",
          "jobTitle": "Geschäftsführer MK-AUSBAU",
          "url": "https://ausbau.nrw/ueber"
        },
        "publisher": {
          "@type": "Organization",
          "name": "MK-AUSBAU",
          "logo": {
            "@type": "ImageObject",
            "url": "https://ausbau.nrw/logo-icon.svg"
          }
        },
        "datePublished": "2026-04-10",
        "description": "Stroh als Dämmung? 🌾 Modern, zertifiziert & extrem klimafreundlich. Alles zu Einblasdämmung mit Stroh, Kosten & Brandschutz im Holzbau.",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": "https://ausbau.nrw/blog/stroh-einblasdaemmung"
        }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Was ist Stroh-Einblasdämmung genau?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Stroh-Einblasdämmung ist aufbereitetes, gereinigtes und entkörntes Getreidestroh (häufig Weizenstroh), das mit Einblasmaschinen in Bauteilhohlräume eingebracht und auf definierte Dichten verdichtet wird. Sie wird vor allem in Holzbau- und Aufstockungsprojekten eingesetzt."
            }
          },
          {
            "@type": "Question",
            "name": "Ist Stroh-Einblasdämmung ohne Zusätze möglich?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Je nach Produkt und System ist Stroh-Einblasdämmung ohne chemische Zusätze möglich. Entscheidend sind die jeweiligen Nachweise/Zulassungen sowie die korrekte Verarbeitung. MK-Ausbau aus Heinsberg (NRW) achtet auf praxistaugliche, sauber nachweisbare Lösungen."
            }
          },
          {
            "@type": "Question",
            "name": "Für welche Gebäude ist Stroh-Einblasdämmung in NRW besonders geeignet?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Besonders geeignet ist Stroh-Einblasdämmung für Holzrahmenbau, Aufstockungen in Holzbauweise, Vorfertigung/Elementbau sowie Dach- und Wandaufbauten mit definierten Gefachen. Im Raum Heinsberg/Niederrhein ist sie interessant, wenn ohnehin Holz- oder Hybridkonstruktionen geplant sind."
            }
          },
          {
            "@type": "Question",
            "name": "Ist Stroh-Einblasdämmung auch für Altbau-Sanierung geeignet?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Ja, vor allem wenn neue Hohlräume geschaffen werden (z. B. Innendämm- oder Installationsebenen im Holzaufbau) oder Dach-/Wandaufbauten erneuert werden. Für klassische Hohlwanddämmungen im Mauerwerk wird material- und projektabhängig entschieden."
            }
          },
          {
            "@type": "Question",
            "name": "Wie wichtig ist Luftdichtheit bei Stroh-Einblasdämmung?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Sehr wichtig. Wie bei anderen Einblasdämmungen müssen luftdichte Ebenen (z. B. OSB/Membranen) und alle Anschlüsse/ Durchdringungen sauber ausgeführt werden. Die Ausführungsqualität entscheidet über Dauerhaftigkeit und Performance."
            }
          },
          {
            "@type": "Question",
            "name": "Welche Einbaudichte braucht Stroh-Einblasdämmung?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Stroh wird typischerweise mit höheren Einbaudichten als viele Standard-Einblasstoffe verarbeitet (system- und projektabhängig). Dadurch wird Setzungssicherheit verbessert und häufig auch Sommerkomfort und Schallschutz. Wichtig sind korrekte Maschineneinstellungen und geschulte Teams."
            }
          },
          {
            "@type": "Question",
            "name": "Wie ist die Dämmwirkung im Vergleich zu Zellulose?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Stroh-Einblasdämmung hat in der Regel einen etwas höheren Lambda-Wert als Zellulose. Für denselben U-Wert wird daher oft etwas mehr Bauteildicke benötigt. Vorteile liegen häufig in Kreislauffähigkeit, biogener CO2-Speicherung und Sommerkomfort."
            }
          },
          {
            "@type": "Question",
            "name": "Wie sieht es mit Brandschutz aus?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Brandschutz wird im Holzbau in der Regel über den gesamten Bauteilaufbau (Beplankung, Bekleidungen, Details) gelöst. Die Einstufung hängt vom jeweiligen System ab. Planung und Ausführung müssen abgestimmt sein, damit die Anforderungen des Projekts erfüllt werden."
            }
          },
          {
            "@type": "Question",
            "name": "Kann Stroh-Einblasdämmung schimmeln?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Biobasierte Dämmstoffe können bei dauerhafter Feuchte geschädigt werden. Bei korrektem Feuchte- und Luftdichtheitskonzept sowie trockenen Bauteilen ist das beherrschbar. Dauerhaft feuchte Zonen und Leckagen sollten vermieden werden."
            }
          },
          {
            "@type": "Question",
            "name": "Wo sollte man Stroh-Einblasdämmung nicht einsetzen?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Typisch ungeeignet sind Perimeter-/Erdkontaktbereiche und dauerhaft feuchte Zonen. Ebenfalls kritisch sind Bauteile ohne schlüssiges Luftdichtheits- und Feuchtekonzept."
            }
          },
          {
            "@type": "Question",
            "name": "Zieht Stroh Schädlinge an?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Professionell aufbereitete Systeme entfernen üblicherweise Restkörner, damit keine Futterquelle in der Konstruktion bleibt. Wie bei jedem Dämmstoff gilt: Gebäudehülle dicht ausführen, Zugangspunkte vermeiden und Details sauber schließen."
            }
          },
          {
            "@type": "Question",
            "name": "Wie läuft ein Projekt mit MK-Ausbau aus Heinsberg ab?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Typisch sind Bauteil-Check und Konzept (Feuchte/Luftdichtheit), Abstimmung des Aufbaus und der Materialwahl, Vorbereitung der Hohlräume/Anschlüsse, Einblasen nach Vorgaben sowie ein abschließender Qualitätscheck und Dokumentation nach Bedarf."
            }
          },
          {
            "@type": "Question",
            "name": "Was kostet Stroh-Einblasdämmung ungefähr?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Die Kosten hängen von Bauteiltyp, Dicke/Volumen, Baustellenlogistik und Detailgrad ab. Naturdämmungen sind nicht immer die günstigste Lösung pro Quadratmeter, können aber über Sommerkomfort, CO2-Bilanz und Kreislauffähigkeit wirtschaftlich attraktiv sein."
            }
          },
          {
            "@type": "Question",
            "name": "Lohnt sich Stroh-Einblasdämmung für mein Projekt in Heinsberg/NRW?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Wenn du in NRW (z. B. Kreis Heinsberg) Holzbau, Aufstockung oder serielle Ausbauprojekte planst und Wert auf kreislauffähige Materialien legst, kann Stroh-Einblasdämmung sehr passend sein. Eine kurze Bauteilprüfung klärt, ob Stroh sinnvoll ist oder ob Alternativen besser passen."
            }
          }
        ]
      }
    ]
  };

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="py-20 bg-white">
      <SEO 
        title="Stroh-Einblasdämmung: Natürlich dämmen in NRW | MK-AUSBAU"
        description="Nachhaltig bauen mit Stroh: CO2-neutral, ETA-zertifiziert & effizient. Ideal für Holzbau & Aufstockung in Heinsberg & NRW. 🌾 Jetzt informieren!"
        keywords="Stroh Einblasdämmung, Stroh Dämmung NRW, ökologische Dämmung Heinsberg, Holzbau Dämmung, nachhaltig Sanieren, Einblasdämmung Kosten"
        schema={articleSchema}
        canonical="/blog/stroh-einblasdaemmung"
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
        <Link 
          to="/blog" 
          className="inline-flex items-center text-mk-teal-600 hover:text-mk-teal-700 font-bold mb-8 transition-colors group"
        >
          <SafeIcon icon={FiArrowLeft} className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Zurück zur Übersicht
        </Link>

        <motion.article {...fadeIn} className="prose prose-lg prose-mk max-w-none">
          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center space-x-3 text-mk-teal-600 font-bold uppercase tracking-wider text-sm mb-4 text-left">
              <SafeIcon icon={FiCloud} />
              <span>Ökologische Baustoffe & Innovation</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight mb-6 text-left">
              Gebäude mit eingeblasenem Stroh: Vom Feld zur Fertigung 🌾
            </h1>
            <p className="text-xl font-medium text-mk-teal-700 mb-6">
              Warum diese natürliche Dämmung für den Mainstream bereit ist – auch in NRW
            </p>
            <div className="flex items-center space-x-4 text-gray-500 text-sm border-y border-gray-100 py-4">
              <span className="flex items-center"><SafeIcon icon={FiShield} className="mr-1" /> Experten-Ratgeber</span>
              <span className="flex items-center"><SafeIcon icon={FiInfo} className="mr-1" /> Stand: April 2026</span>
            </div>
          </header>

          {/* Intro */}
          <div className="lead text-xl text-gray-600 leading-relaxed mb-12 text-left">
            Wenn Sie ein Dämmmaterial suchen, das CO₂ bindet, aus einem erneuerbaren Nebenstrom kommt und am Lebensende ohne komplizierte Trennprozesse wieder in biologische Kreisläufe zurückkann, klingt die Antwort fast zu simpel: <strong>Stroh</strong>.
          </div>

          <p className="text-gray-700">
            Und genau diese Simplizität ist gerade der Punkt. Moderne Stroh-Einblasdämmstoffe werden heute so aufbereitet, dass sie <strong>planbar, lieferfähig und normtauglich</strong> werden – inklusive europäischer Zulassungen.
          </p>

          <p className="text-gray-700 mb-8">
            Im Folgenden bekommst du ein praxisnahes Playbook: Warum Stroh jetzt Sinn macht, wie es verarbeitet wird, worauf Planer und Bauherren achten müssen – und welche Einsatzfälle im Holzbau (und in der Sanierung) wirklich passen. Dazu mit Blick auf NRW: In Regionen wie Heinsberg, Aachen, Mönchengladbach oder Düsseldorf wächst die Nachfrage nach Holz- und Hybridbau – ein Umfeld, in dem MK-Ausbau solche Lösungen als Teil moderner Strategien einordnet.
          </p>

          {/* Warum Stroh? */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">Warum Stroh – warum jetzt?</h2>
          
          <div className="space-y-8">
            <div className="bg-mk-teal-50 p-6 rounded-xl border-l-4 border-mk-teal-500">
              <h3 className="font-bold text-mk-teal-900 text-lg mb-2">1. Klima-Logik, die man erklären kann</h3>
              <p className="text-mk-teal-800 text-sm m-0">
                Stroh entsteht als Nebenprodukt im Getreideanbau. Wenn es ungenutzt verrottet, geht der gebundene Kohlenstoff relativ schnell wieder in die Atmosphäre. Wird Stroh stattdessen in Bauteilen über Jahrzehnte „eingelagert“, wird aus einem kurzfristigen biogenen Kreislauf <strong>Langzeitspeicherung</strong> – plus der klassische Effekt: weniger Heizenergie durch Dämmung.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
              <h3 className="font-bold text-gray-900 text-lg mb-2">2. „Es ist Stroh und sonst nichts“</h3>
              <p className="text-gray-600 text-sm m-0">
                Der Charme der Materialreinheit: Bei modernen Stroh-Einblasdämmungen ist der Anspruch häufig, <strong>ohne chemische Zusätze</strong> auszukommen. Das wird in Produktbeschreibungen explizit so kommuniziert – u. a. auch mit Verweis auf Prüfungen zur Brandklasse.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
              <h3 className="font-bold text-gray-900 text-lg mb-2">3. Mainstream-Faktor: Zulassung</h3>
              <p className="text-gray-600 text-sm m-0">
                Ein echter Türöffner ist, dass es inzwischen <strong>europäische technische Zulassungen (ETA)</strong> für Stroh-Einblasdämmungen gibt – das macht Planung und Ausschreibung wesentlich einfacher.
              </p>
            </div>
          </div>

          {/* Vom Feld zur Faser */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">Vom Feld zur „Faser“: Beschaffung, Reinigung, Qualität</h2>
          <p className="text-gray-700 mb-6">
            Damit Stroh als Dämmstoff funktioniert, ist die Prozesskette entscheidend:
          </p>
          <ul className="space-y-3 list-none pl-0">
            <li className="flex items-start">
              <SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-3 mt-1 flex-shrink-0" />
              <span><strong>Rohstoffauswahl:</strong> Stroh muss trocken, sauber und frei von Schimmel-/Fäulnisanzeichen sein.</span>
            </li>
            <li className="flex items-start">
              <SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-3 mt-1 flex-shrink-0" />
              <span><strong>Reinigung/Entkörnung:</strong> Moderne Prozesse entfernen Restkörner – wichtig, damit in Hohlräumen keine „Futterquelle“ entsteht.</span>
            </li>
            <li className="flex items-start">
              <SafeIcon icon={FiCheck} className="text-mk-teal-500 mr-3 mt-1 flex-shrink-0" />
              <span><strong>Logistik:</strong> Stroh-Einblasdämmung wird üblicherweise gepresst in Säcken auf Paletten geliefert – baustellentauglich für Vorfertiger und Taktung.</span>
            </li>
          </ul>

          {/* Einbau in der Praxis */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiTruck} className="mr-3 text-mk-teal-600" />
            Einbau in der Praxis: Der Knackpunkt heißt Dichte
          </h2>
          <p className="text-gray-700 mb-6">
            Einblasdämmung ist für viele Teams in NRW nichts Exotisches. Für eingeblasenes Stroh sind jedoch <strong>hohe Einbaudichten</strong> typisch – Größenordnungen um <strong>105 kg/m³</strong> werden oft genannt.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-green-50 p-6 rounded-xl">
              <h4 className="font-bold text-green-800 mb-3 flex items-center"><SafeIcon icon={FiTrendingUp} className="mr-2"/> Pluspunkte</h4>
              <ul className="text-sm text-green-900 space-y-2">
                <li>• Bessere Sommer-Performance (Trägheit)</li>
                <li>• Oft sehr ordentlicher Schallschutz durch Masse</li>
              </ul>
            </div>
            <div className="bg-orange-50 p-6 rounded-xl">
              <h4 className="font-bold text-orange-800 mb-3 flex items-center"><SafeIcon icon={FiAlertTriangle} className="mr-2"/> Herausforderungen</h4>
              <ul className="text-sm text-orange-900 space-y-2">
                <li>• Mehr Materialhandling (Masse!)</li>
                <li>• Ausführung muss extrem sauber sein (Hohlraumkontinuität)</li>
              </ul>
            </div>
          </div>
          <p className="bg-gray-50 p-4 rounded-lg text-sm text-gray-700 italic border-l-4 border-gray-300">
            <strong>Praxis-Tipp:</strong> Wenn ihr sowieso vorfertigt (Holzrahmenelemente), kann Stroh werksseitig eingeblasen werden.
          </p>

          {/* Leistung */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">Leistung: Wärme, Feuer, Bauphysik – ehrlich eingeordnet</h2>
          
          <div className="space-y-6 mb-12">
            <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
              <h3 className="font-bold text-mk-teal-600 mb-2 flex items-center"><SafeIcon icon={FiThermometer} className="mr-2"/> Wärmeleitfähigkeit (λ)</h3>
              <p className="text-gray-700 text-sm">
                Bei Stroh-Einblasdämmungen wird häufig eine WLS um <strong>050</strong> kommuniziert. <br/>
                <strong>Was das heißt:</strong> Stroh ist nicht automatisch „besser“ als Zellulose beim Dämmwert. Der Vorteil liegt in der Kreislauffähigkeit, der CO₂-Story und dem Sommerkomfort.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
              <h3 className="font-bold text-mk-red-600 mb-2 flex items-center"><SafeIcon icon={FiAlertTriangle} className="mr-2"/> Brandverhalten</h3>
              <p className="text-gray-700 text-sm">
                Oft wird Brandklasse E (EN 13501-1) genannt. <br/>
                <strong>Wichtig:</strong> Brandanforderungen werden im Holzbau regelmäßig über den <strong>Schichtenaufbau</strong> (Beplankung/Gipsfaser) erreicht – Stroh ist ein Baustein, nicht die alleinige Brandstrategie.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm">
              <h3 className="font-bold text-blue-600 mb-2 flex items-center"><SafeIcon icon={FiCloud} className="mr-2"/> Feuchte & Details</h3>
              <p className="text-gray-700 text-sm">
                Stroh ist biobasiert. Goldene Regel: <strong>Dauerhaft feuchte Zonen vermeiden.</strong> Im Sockel/Perimeterbereich werden deshalb andere Materialien eingesetzt.
              </p>
            </div>
          </div>

          {/* Wo passt es? */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6">Wo passt eingeblasenes Stroh wirklich gut?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">1. Holzrahmenbau (Neubau & Aufstockung)</h3>
              <p className="text-gray-600 text-sm mb-3">
                Gerade in NRW (auch rund um Heinsberg/Aachen) sieht man mehr Aufstockungen und serielle Sanierung.
              </p>
              <p className="text-mk-teal-700 font-bold text-sm">
                Hier spielt Stroh seine Stärken aus: schneller Einbau & gute Story für nachhaltige Projekte.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">2. Sanierung – aber zielgerichtet</h3>
              <p className="text-gray-600 text-sm mb-3">
                Für klassische Hohlwanddämmung (schmaler Spalt) oft nicht die erste Wahl. Aber bei <strong>Holzständer-Innendämmungen</strong> oder neuen Dachaufbauten sehr sinnvoll.
              </p>
            </div>
          </div>

          {/* Checkliste */}
          <h2 className="text-3xl font-bold text-gray-900 mt-16 mb-6 flex items-center">
            <SafeIcon icon={FiList} className="mr-3 text-mk-teal-600" />
            Die Checkliste vor dem Start
          </h2>
          <div className="bg-gray-50 p-8 rounded-2xl mb-12">
            <ul className="space-y-4">
              {[
                "Bauteil passt? (Kein Sockel/Perimeter, keine Nässe)",
                "Luftdichtheitskonzept klar? (Membran/OSB)",
                "Einbaudichte definiert? (Nicht „Pi mal Daumen“)",
                "Installateur hat Erfahrung/Schulung?",
                "Hohlraumkontinuität gesichert? (Keine „toten Ecken“)"
              ].map((item, i) => (
                <li key={i} className="flex items-center text-gray-800 font-medium">
                  <SafeIcon icon={FiCheckCircle} className="text-mk-teal-500 mr-3 w-5 h-5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* FAQ Section from Prompt */}
          <div className="mt-20 pt-12 border-t border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              FAQ: Stroh-Einblasdämmung in NRW & Heinsberg
            </h2>
            <div className="space-y-4">
              {[
                {
                  q: "Was ist Stroh-Einblasdämmung genau?",
                  a: "Stroh-Einblasdämmung ist aufbereitetes, gereinigtes und entkörntes Getreidestroh (meist Weizen), das als lose Dämmung mit Einblasmaschinen in Bauteilhohlräume eingebracht wird. Ziel ist eine definierte Einbaudichte, damit die Dämmung dauerhaft formstabil bleibt."
                },
                {
                  q: "Ist Stroh-Einblasdämmung “nur Stroh” oder sind Zusätze drin?",
                  a: "Je nach Produkt kann Stroh-Einblasdämmung ohne chemische Zusätze auskommen. Entscheidend ist das jeweilige System (Zulassung/Nachweise) und die korrekte Verarbeitung."
                },
                {
                  q: "Für welche Gebäude ist sie besonders geeignet?",
                  a: "Am häufigsten passt sie bei Holzrahmenbau (Neubau), Aufstockungen in Holzbauweise und Vorfertigung. Im Kreis Heinsberg ist sie besonders interessant, wenn ohnehin eine Holz- oder Hybridkonstruktion geplant ist."
                },
                {
                  q: "Kann Stroh-Einblasdämmung schimmeln?",
                  a: "Wie jedes biobasierte Material ist es empfindlich gegenüber dauerhafter Feuchte. Wenn Bauteile korrekt geplant sind (Feuchteschutz, Luftdichtheit) ist das beherrschbar. Entscheidend: keine feuchten Sockel-/Erdkontaktzonen."
                },
                {
                  q: "Zieht Stroh Schädlinge an?",
                  a: "Professionell aufbereitete Systeme entfernen üblicherweise Restkörner, damit keine “Futterquelle” bleibt. Wie bei jedem Dämmstoff gilt: Gebäudehülle dicht ausführen, Zugangspunkte vermeiden."
                },
                {
                  q: "Was kostet Stroh-Einblasdämmung ungefähr?",
                  a: "Wegen höherer Einbaudichten kann der Material- und Handlinganteil höher liegen als bei Zellulose – dafür stehen dem CO₂- und Kreislaufvorteile gegenüber. Naturdämmungen sind oft nicht die billigste Lösung pro m², aber wirtschaftlich attraktiv bei Nachhaltigkeitszielen."
                }
              ].map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-6 hover:bg-mk-teal-50 transition-colors cursor-default">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-start">
                    <SafeIcon icon={FiHelpCircle} className="text-mk-teal-500 mt-1 mr-3 flex-shrink-0" />
                    {faq.q}
                  </h3>
                  <p className="text-gray-700 text-sm ml-8 leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center py-12 mt-12 border-t border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Interesse an Stroh-Dämmung?</h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Wir prüfen, ob Stroh für Ihr Projekt in Heinsberg oder NRW geeignet ist. Ehrlich, technisch fundiert und mit Blick auf Ihr Budget.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/kontakt"
                className="bg-mk-teal-500 hover:bg-mk-teal-600 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Beratung anfragen
              </Link>
              <Link
                to="/daemmtechnik"
                className="bg-mk-red-700 hover:bg-mk-red-800 text-white px-8 py-4 rounded-sm font-bold transition-all shadow-lg flex items-center justify-center"
              >
                Mehr zu Dämmtechnik
              </Link>
            </div>
          </div>
        </motion.article>
      </div>

      <RelatedTopics />
      <ServicesNavigationFooter />
      <GoogleMaps />
    </div>
  );
}

export default StrawInsulation;